// 4.5 Inspector: empty = document defaults; single = common + typed block + style;
// multi = shared properties. All length fields unit-parse per DM-8.
import { useRef, useState } from "preact/hooks";
import type { AppApi } from "../appApi";
import type { ToolManager } from "../tools/toolManager";
import type { Dash, Doc, Entity, FillMode, FontName, Style, TextStyle, Units } from "../doc/entities";
import { formatLength, parseScalar } from "../core/units";
import { entityBounds } from "../engine/bounds";
import { applyStyleCommand, componentColourCommand, componentColours, componentWidth, componentWidthCommand } from "../tools/editOps";
import { isLocked, moveCommand, rotatePatches, rotationOf, scalePatches, setRotationPatches } from "../tools/entityOps";
import { movableIds, objectBox, spaceAxis, spacingNow, type AlignEdge, type Side, type SpaceMode } from "../tools/align";
import { PIVOT_SPOT_NAMES, PIVOT_SPOTS, pivotOf, spotPoint, turnPoint } from "../tools/pivot";
import { geometryBounds, resizePoints } from "../tools/byNumbers";
import { angleOf, dir, dist, sub } from "../core/geometry";
import { pipeLength, pipeMetaOf, portsOf, type PipeAnchor, type PipeEndStyle } from "../pipes/api";
import { looksLikeFitting, looksLikePipe, ownerName, PIPE_TOOL_SIZES, portIsFree, safePorts } from "../tools/pipeTool";
import { FITTING_SIZE_CHOICES, fittingInfoOf, isTeeKind } from "../pipes/resizeFitting";
import { isLintPot, placedLintPot, withAnotherEnd } from "../pipes/lintPot";
import { butterflyInfoOf } from "../pipes/butterfly";
import { DOOR_WORDS, WALL_THICKNESSES, doorSpan, spineOf, wallGeometry, wallMetaOf, wallNetwork, type DoorKind, type EndJoin, type WallJustify } from "../walls/wall";
import { butterflyRowFor, hasOperator, type ButterflySpec } from "../library/butterflyValves";
import { LINTPOT_LIMITS, SEEN_FROM, chamberTop, defaultBend, endHeight, lintPotHeight, normAngle, seenFromAngle, seenFromWords, type LintPotEnd, type LintPotSpec } from "../library/lintPots";

/**
 * A box that keeps what you type. The Inspector re-draws whenever the mouse moves over the
 * drawing; a plain controlled box then puts the OLD number back and Tab saves nothing
 * (owner, 2026-10-02). This one holds the text you typed until you finish:
 * Enter or Tab or clicking away saves it, Esc puts the old value back.
 * onCommit returns false to refuse the text (the old value comes back).
 */
export function DraftInput({
  value,
  onCommit,
  ...attrs
}: {
  value: string;
  onCommit: (raw: string) => boolean | void;
  class?: string;
  id?: string;
  title?: string;
  type?: string;
  step?: string;
  min?: string;
  max?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const dirty = useRef(false);
  const finish = (el: HTMLInputElement, commit: boolean): void => {
    const raw = el.value;
    const wasDirty = dirty.current;
    dirty.current = false;
    setDraft(null);
    if (!commit || !wasDirty || raw === value) {
      el.value = value;
      return;
    }
    if (onCommit(raw) === false) el.value = value;
  };
  return (
    <input
      {...attrs}
      value={draft ?? value}
      onInput={(e) => {
        dirty.current = true;
        setDraft((e.currentTarget as HTMLInputElement).value);
      }}
      onKeyDown={(e) => {
        e.stopPropagation(); // typing here is not a canvas shortcut
        const el = e.currentTarget as HTMLInputElement;
        if (e.key === "Enter") {
          finish(el, true);
          e.preventDefault();
        } else if (e.key === "Tab") {
          finish(el, true); // save before the focus moves on (Shift+Tab too)
        } else if (e.key === "Escape") {
          finish(el, false);
        }
      }}
      onBlur={(e) => finish(e.currentTarget as HTMLInputElement, true)}
    />
  );
}

function LenField({
  label,
  mm,
  doc,
  onCommit,
  allowNegative = true,
  id,
  disabled,
  title
}: {
  label: string;
  mm: number;
  doc: Doc;
  onCommit: (mm: number) => void;
  allowNegative?: boolean;
  id?: string;
  disabled?: boolean;
  title?: string;
}) {
  const [invalid, setInvalid] = useState(false);
  const display = formatLength(mm, doc.units, doc.precision);
  return (
    <label class="insp-field">
      <span>{label}</span>
      <DraftInput
        id={id}
        disabled={disabled}
        title={title}
        class={invalid ? "invalid" : ""}
        value={display}
        onCommit={(raw) => {
          const parsed = parseScalar(raw, doc.units);
          if (parsed === null || (!allowNegative && parsed <= 0)) {
            setInvalid(true); // EC-DM-5/6
            setTimeout(() => setInvalid(false), 300);
            return false;
          }
          onCommit(parsed);
        }}
      />
    </label>
  );
}

function StyleEditor({ style, onChange }: { style: Style; onChange: (s: Style) => void }) {
  const set = (patch: Partial<Style>) => onChange({ ...structuredClone(style), ...patch });
  const setFill = (patch: Partial<Style["fill"]>) =>
    onChange({ ...structuredClone(style), fill: { ...style.fill, ...patch } });
  return (
    <div class="style-editor">
      <label class="insp-field">
        <span>Stroke</span>
        <span class="insp-inline">
          <input type="color" aria-label="Stroke color" value={style.strokeColor} onChange={(e) => set({ strokeColor: (e.target as HTMLInputElement).value })} />
          <DraftInput
            class="narrow"
            type="number"
            min="0.05"
            step="0.05"
            value={String(style.strokeWidth)}
            title={style.widthMode === "world" ? "Width (mm)" : "Width (px)"}
            onCommit={(raw) => {
              const v = Number(raw);
              if (!(v > 0)) return false;
              set({ strokeWidth: v });
            }}
          />
          <select value={style.dash} onChange={(e) => set({ dash: (e.target as HTMLSelectElement).value as Dash })}>
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
            <option value="dotted">Dotted</option>
            <option value="dashdot">Dash-dot</option>
          </select>
        </span>
      </label>
      <label class="insp-field">
        <span>Width is</span>
        <select
          value={style.widthMode}
          title="World widths scale with zoom/print; screen widths stay constant"
          onChange={(e) => set({ widthMode: (e.target as HTMLSelectElement).value as Style["widthMode"] })}
        >
          <option value="world">mm (scales)</option>
          <option value="screen">px (constant)</option>
        </select>
      </label>
      <label class="insp-field">
        <span>Fill</span>
        <span class="insp-inline">
          <select value={style.fill.mode} onChange={(e) => setFill({ mode: (e.target as HTMLSelectElement).value as FillMode })}>
            <option value="none">None</option>
            <option value="solid">Solid</option>
            <option value="hatch">Hatch</option>
            <option value="crosshatch">Crosshatch</option>
            <option value="dots">Dots</option>
          </select>
          <input type="color" aria-label="Fill color" value={style.fill.color} onChange={(e) => setFill({ color: (e.target as HTMLInputElement).value })} />
        </span>
      </label>
      <label class="insp-field">
        <span>Fill opacity</span>
        <input
          type="range"
          min="0"
          max="100"
          value={Math.round(style.fill.opacity * 100)}
          onChange={(e) => setFill({ opacity: Number((e.target as HTMLInputElement).value) / 100 })}
        />
      </label>
    </div>
  );
}

function PerspectivePanel({ tools }: { tools: ToolManager }) {
  const tool = tools.getPerspective();
  const state = tool.panelState();
  return (
    <div class="inspector">
      <div class="insp-title">Perspective correction</div>
      <small class="muted">
        Drag the four pins to the corners of something that should be rectangular (a door, a sheet
        edge, a tile).
      </small>
      <label class="insp-field">
        <span>Target</span>
        <span class="insp-inline">
          <label class="insp-check">
            <input type="radio" checked={state.mode === "auto"} onChange={() => tool.setMode("auto")} />
            auto rectangle
          </label>
          <label class="insp-check">
            <input type="radio" checked={state.mode === "size"} onChange={() => tool.setMode("size")} />
            known size
          </label>
        </span>
      </label>
      {state.mode === "size" && (
        <label class="insp-field">
          <span>Real size</span>
          <span class="insp-inline">
            <DraftInput
              class="narrow"
              type="number"
              min="1"
              value={String(Math.round(state.widthMm))}
              title="Width (mm)"
              onCommit={(raw) => tool.setSize(Number(raw), state.heightMm)}
            />
            ×
            <DraftInput
              class="narrow"
              type="number"
              min="1"
              value={String(Math.round(state.heightMm))}
              title="Height (mm)"
              onCommit={(raw) => tool.setSize(state.widthMm, Number(raw))}
            />
            mm
          </span>
        </label>
      )}
      {!state.validation.ok && <small class="muted" style="color: var(--danger)">{state.validation.reason}</small>}
      {state.mode === "size" && (
        <small class="muted">Known size also calibrates the image — traced lines become real-scale.</small>
      )}
      <div class="insp-actions">
        <button disabled={!state.validation.ok} onClick={() => tool.apply()}>
          Apply
        </button>
        <button onClick={() => tool.exit()}>Cancel</button>
        {state.hasCorrection && (
          <button class="danger" onClick={() => tool.removeCorrection()}>
            Remove correction
          </button>
        )}
      </div>
    </div>
  );
}

/** F2: font/size/weight/colour controls shared by every text-bearing element. */
function TextStyleBlock({
  ts,
  doc,
  label = "Text",
  onChange
}: {
  ts: TextStyle;
  doc: Doc;
  label?: string;
  onChange: (next: TextStyle) => void;
}) {
  return (
    <>
      <label class="insp-field">
        <span>{label} font</span>
        <span class="insp-inline">
          <select
            value={ts.font}
            onChange={(e) => onChange({ ...ts, font: (e.target as HTMLSelectElement).value as FontName })}
          >
            <option value="Inter">Inter</option>
            <option value="Caveat">Caveat</option>
            <option value="JetBrains Mono">JetBrains Mono</option>
          </select>
          <select
            value={ts.bold ? "bold" : "regular"}
            title="Stroke weight"
            onChange={(e) => onChange({ ...ts, bold: (e.target as HTMLSelectElement).value === "bold" })}
          >
            <option value="regular">Regular</option>
            <option value="bold">Bold</option>
          </select>
        </span>
      </label>
      <LenField
        label={`${label} size`}
        mm={ts.size}
        doc={doc}
        allowNegative={false}
        onCommit={(v) => onChange({ ...ts, size: v })}
      />
      <label class="insp-field">
        <span>{label} color</span>
        <input
          type="color"
          aria-label={`${label} color`}
          value={ts.color}
          onChange={(e) => onChange({ ...ts, color: (e.target as HTMLInputElement).value })}
        />
      </label>
    </>
  );
}

/** F2: highlighter creation settings — size, colour, opacity (persisted prefs). */
function HighlighterBlock({ tools }: { tools: ToolManager }) {
  const h = tools.prefs.highlighter;
  const set = (patch: Partial<typeof h>) => tools.setPref({ highlighter: { ...h, ...patch } });
  return (
    <div class="inspector">
      <div class="insp-title">Highlighter</div>
      <label class="insp-field">
        <span>Size</span>
        <span class="insp-inline">
          <input
            type="range"
            min="1"
            max="24"
            value={h.width}
            onChange={(e) => set({ width: Number((e.target as HTMLInputElement).value) })}
          />
          <small class="muted">{h.width}px</small>
        </span>
      </label>
      <label class="insp-field">
        <span>Colour</span>
        <input
          type="color"
          aria-label="Highlighter colour"
          value={h.color}
          onChange={(e) => set({ color: (e.target as HTMLInputElement).value })}
        />
      </label>
      <label class="insp-field">
        <span>Opacity</span>
        <span class="insp-inline">
          <input
            type="range"
            min="10"
            max="100"
            value={Math.round(h.opacity * 100)}
            onChange={(e) => set({ opacity: Number((e.target as HTMLInputElement).value) / 100 })}
          />
          <small class="muted">{Math.round(h.opacity * 100)}%</small>
        </span>
      </label>
      <small class="muted">Applies to new highlighter strokes. Existing strokes keep their look.</small>
    </div>
  );
}

/**
 * The turn of one object, in degrees clockwise on screen, as a box you can type in.
 * Text, pictures and grids store it. Line-work and groups remember the turns applied to them.
 */
function RotationField({ api, id }: { api: AppApi; id: string }) {
  const entity = api.store.doc.entities[id];
  if (!entity) return null;
  const shown = Number(rotationOf(entity).toFixed(2)).toString();
  return (
    <label class="insp-field">
      <span>Rotation °</span>
      <DraftInput
        id="insp-rotation"
        class="narrow"
        value={shown}
        title="Turn of this object in degrees, clockwise on screen. Type a number and press Enter or Tab. 0 = as drawn or placed."
        onCommit={(raw) => {
          const v = Number.parseFloat(raw);
          if (!Number.isFinite(v)) return false;
          const patches = setRotationPatches(api.store.doc, id, v);
          if (patches.length) api.store.dispatch({ kind: "updateEntities", patches });
        }}
      />
    </label>
  );
}

/**
 * The point this object turns about. Not set = the centre of its box. Nine buttons put it on a
 * corner, the middle of a side or the centre; X and Y take exact numbers; Pick point takes a
 * click on the drawing (snaps work). The marker can also be dragged on the drawing.
 */
function PivotField({ api, tools, id }: { api: AppApi; tools: ToolManager; id: string }) {
  const doc = api.store.doc;
  const entity = doc.entities[id];
  const box = objectBox(doc, id);
  if (!entity || !box) return null;
  const own = pivotOf(entity);
  const centre = spotPoint(box, "c");
  const shown = own ?? centre;
  const near = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.abs(a.x - b.x) < 1e-6 && Math.abs(a.y - b.y) < 1e-6;
  const picking = tools.pickingPivot === id;
  return (
    <div class="pivot-block">
      <div class="insp-field">
        <span>Turns about</span>
        <span class="muted" id="pivot-state">
          {own ? "a set point" : "the centre"}
        </span>
      </div>
      <div class="pivot-grid" role="group" aria-label="Put the turning point on the box">
        {PIVOT_SPOTS.map((spot) => {
          const p = spotPoint(box, spot);
          const on = own ? near(own, p) : spot === "c";
          return (
            <button
              key={spot}
              data-pivot={spot}
              aria-pressed={on}
              class={on ? "on" : ""}
              title={`Turn about the ${PIVOT_SPOT_NAMES[spot]}`}
              onClick={() => tools.setPivot(id, spot === "c" ? null : p)}
            >
              <span aria-hidden="true" />
            </button>
          );
        })}
      </div>
      <LenField label="Point X" mm={shown.x} doc={doc} onCommit={(v) => tools.setPivot(id, { x: v, y: shown.y })} />
      <LenField label="Point Y" mm={shown.y} doc={doc} onCommit={(v) => tools.setPivot(id, { x: shown.x, y: v })} />
      <div class="insp-actions">
        <button id="pivot-pick" onClick={() => (picking ? tools.cancelPickPivot() : tools.startPickPivot(id))}>
          {picking ? "Click the drawing…" : "Pick point"}
        </button>
        <button id="pivot-reset" disabled={!own} title="Turn about the centre of the box again" onClick={() => tools.setPivot(id, null)}>
          Centre
        </button>
      </div>
    </div>
  );
}

/**
 * Align the selection: line up edges or centres, centre on another object, place beside it,
 * spread out. "Pick object" chooses the object to line up with; that object stays where it is.
 */
function AlignBlock({ api, tools }: { api: AppApi; tools: ToolManager }) {
  const doc = api.store.doc;
  const [gap, setGap] = useState(tools.alignGap);
  const [note, setNote] = useState<string | null>(null);
  const ref = tools.alignRef && doc.entities[tools.alignRef] ? tools.alignRef : null;
  const ids = movableIds(doc, tools.selection.all, ref);
  const canSelf = ids.length >= 2;
  const canAlign = ref !== null || canSelf;
  const run = (op: Parameters<ToolManager["alignSelection"]>[0]) => setNote(tools.alignSelection(op));
  const edge = (e: AlignEdge, label: string, title: string) => (
    <button data-align={e} disabled={!canAlign} title={title} onClick={() => run({ kind: "edge", edge: e })}>
      {label}
    </button>
  );
  const beside = (side: Side, label: string, title: string) => (
    <button data-beside={side} disabled={ref === null} title={title} onClick={() => run({ kind: "beside", side })}>
      {label}
    </button>
  );
  return (
    <div class="align-block">
      <div class="insp-title">Align</div>
      <div class="align-ref">
        <span class="muted">
          To:{" "}
          {ref
            ? `${doc.entities[ref]!.type} (orange box)`
            : canSelf
              ? "the selection"
              : "pick an object"}
        </span>
        <button id="align-pick" onClick={() => (tools.pickingAlignRef ? tools.cancelPickAlignRef() : tools.startPickAlignRef())}>
          {tools.pickingAlignRef ? "Click an object…" : ref ? "Change" : "Pick object"}
        </button>
        {ref && (
          <button id="align-clear" onClick={() => tools.clearAlignRef()} title="Line up with the selection again">
            Clear
          </button>
        )}
      </div>
      <div class="align-grid">
        {edge("left", "Left edge", "Line up the left edges")}
        {edge("hcenter", "Centre", "Line up the centres, left to right")}
        {edge("right", "Right edge", "Line up the right edges")}
        {edge("top", "Top edge", "Line up the top edges")}
        {edge("vcenter", "Middle", "Line up the centres, top to bottom")}
        {edge("bottom", "Bottom edge", "Line up the bottom edges")}
      </div>
      <div class="insp-actions">
        <button data-align="centre" disabled={!canAlign} title="Put the centre on the centre, both ways" onClick={() => run({ kind: "centre" })}>
          Centre both ways
        </button>
        <button data-distribute="h" disabled={ids.length < 3} title="Equal gaps, left to right (3 or more objects)" onClick={() => run({ kind: "distribute", axis: "h" })}>
          Spread across
        </button>
        <button data-distribute="v" disabled={ids.length < 3} title="Equal gaps, top to bottom (3 or more objects)" onClick={() => run({ kind: "distribute", axis: "v" })}>
          Spread down
        </button>
      </div>
      <SpaceEvenly doc={doc} tools={tools} ids={ids} onNote={setNote} />
      <LenField
        label="Gap"
        mm={gap}
        doc={doc}
        onCommit={(v) => {
          tools.alignGap = v;
          setGap(v);
        }}
      />
      <div class="align-grid align-grid-4">
        {beside("left", "Left of", "Place the selection against the left side of the picked object, a gap apart")}
        {beside("right", "Right of", "Place the selection against the right side of the picked object, a gap apart")}
        {beside("above", "Above", "Place the selection above the picked object, a gap apart")}
        {beside("below", "Below", "Place the selection below the picked object, a gap apart")}
      </div>
      <small class="muted" id="align-note">
        {note ?? (ref ? "Each selected object lines up with the orange one." : "Select two or more objects, or pick the object to line up with.")}
      </small>
    </div>
  );
}

/**
 * SPACE EVENLY (3.27.0). Owner, 2026-10-23: "select a group of objects and have them spaced evenly with a defined gap
 * between outer edges or between the objects' centre lines … I want to change the spacing between each object to be
 * wider, pushing the set down." The first object (top, or left) stays. Objects side by side across the line (a tank
 * and its valve) move together.
 */
function SpaceEvenly({ doc, tools, ids, onNote }: { doc: Doc; tools: ToolManager; ids: string[]; onNote: (s: string | null) => void }) {
  const [mode, setMode] = useState<SpaceMode>(tools.spaceMode);
  const [value, setValue] = useState(tools.spaceValue);
  const axis = spaceAxis(doc, ids);
  const now = ids.length >= 2 ? spacingNow(doc, ids, axis) : null;
  const range = (xs: number[]): string => {
    if (!xs.length) return "";
    const lo = Math.min(...xs);
    const hi = Math.max(...xs);
    const f = (v: number) => formatLength(v, doc.units, doc.precision);
    return Math.abs(hi - lo) < 0.05 ? f(lo) : `${f(lo)} to ${f(hi)}`;
  };
  const can = now !== null && now.units >= 2;
  const run = (a: "h" | "v") => onNote(tools.alignSelection({ kind: "space", axis: a, mode, value }));
  return (
    <div class="space-evenly" id="space-evenly">
      <div class="insp-title">Space evenly</div>
      <label class="insp-field">
        <span>Measure</span>
        <select
          id="space-mode"
          value={mode}
          title="What the distance measures"
          onChange={(e) => {
            const m = (e.target as HTMLSelectElement).value as SpaceMode;
            tools.spaceMode = m;
            setMode(m);
          }}
        >
          <option value="gap" title="The gap between the outer edges of next objects">Edge to edge</option>
          <option value="pitch" title="The distance between the centre lines of next objects">Centre to centre</option>
        </select>
      </label>
      <LenField
        id="space-value"
        label="Distance"
        mm={value}
        doc={doc}
        title={mode === "gap" ? "The gap between the edges of next objects" : "The distance between the centre lines of next objects"}
        onCommit={(v) => {
          tools.spaceValue = v;
          setValue(v);
        }}
      />
      <div class="insp-actions">
        <button id="space-down" disabled={!can} title="The top object stays. Each next object moves down or up to the distance." onClick={() => run("v")}>
          Space down
        </button>
        <button id="space-across" disabled={!can} title="The left object stays. Each next object moves right or left to the distance." onClick={() => run("h")}>
          Space across
        </button>
      </div>
      {can && (
        <small class="muted" id="space-now">
          Now, {now.units} in a {axis === "v" ? "column" : "row"}: gaps {range(now.gaps)}; centre to centre {range(now.pitches)}.
        </small>
      )}
    </div>
  );
}

/** Rotate/scale the selection about its bbox centre — same builders the canvas handles use. */
function TransformBlock({ api, tools }: { api: AppApi; tools: ToolManager }) {
  const doc = api.store.doc;
  const ids = [...tools.selection.all].filter((id) => !isLocked(doc, id));
  if (ids.length === 0) return null;
  const center = (() => {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const id of ids) {
      const e = doc.entities[id];
      if (!e) continue;
      const b = entityBounds(e, doc);
      if (b.minX > b.maxX) continue;
      minX = Math.min(minX, b.minX);
      minY = Math.min(minY, b.minY);
      maxX = Math.max(maxX, b.maxX);
      maxY = Math.max(maxY, b.maxY);
    }
    return minX > maxX ? null : { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  })();
  if (!center) return null;
  const apply = (el: HTMLInputElement, kind: "rotate" | "scale") => {
    const v = Number.parseFloat(el.value);
    if (!Number.isFinite(v) || (kind === "scale" && v <= 0) || (kind === "rotate" && v === 0) || (kind === "scale" && v === 1)) {
      el.value = "";
      return;
    }
    // one object with a set turning point turns about it; scaling stays about the centre
    const about = kind === "rotate" ? (turnPoint(doc, ids) ?? center) : center;
    const patches = kind === "rotate" ? rotatePatches(doc, ids, about, v) : scalePatches(doc, ids, center, v);
    if (patches.length) api.store.dispatch({ kind: "updateEntities", patches });
    el.value = "";
  };
  const field = (label: string, kind: "rotate" | "scale", placeholder: string, title: string) => (
    <label class="insp-field">
      <span>{label}</span>
      <input
        class="narrow"
        type="number"
        step="any"
        placeholder={placeholder}
        title={title}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            apply(e.currentTarget as HTMLInputElement, kind);
            e.preventDefault();
          }
          e.stopPropagation();
        }}
        onBlur={(e) => {
          if ((e.currentTarget as HTMLInputElement).value.trim() !== "") {
            apply(e.currentTarget as HTMLInputElement, kind);
          }
        }}
      />
    </label>
  );
  return (
    <>
      {field("Rotate by", "rotate", "deg ⏎", "Rotate about the turning point of one object, or the selection centre (degrees, + = clockwise)")}
      {field("Scale by", "scale", "factor ⏎", "Uniform scale about the selection centre (e.g. 2 or 0.5)")}
    </>
  );
}

// ---- pipes (owner, 2026-10-06) ------------------------------------------------------------

const END_NAMES: Record<PipeEndStyle, string> = {
  none: "None (joined)",
  flat: "Flat",
  circle: "Circle — floor penetration",
  flex: "Rubber joint — flexible coupler"
};

/**
 * A length box and a button. Enter in the box or a press on the button runs `onGo` with the
 * length in millimetres. The box keeps what you typed while the Inspector re-draws.
 */
function LengthAction({
  idLen,
  idBtn,
  button,
  title,
  doc,
  initialMm = 1000,
  disabled = false,
  onGo
}: {
  idLen: string;
  idBtn: string;
  button: string;
  title: string;
  doc: Doc;
  initialMm?: number;
  disabled?: boolean;
  onGo: (mm: number) => void;
}) {
  const [text, setText] = useState(() => formatLength(initialMm, doc.units, doc.precision));
  const [invalid, setInvalid] = useState(false);
  const box = useRef<HTMLInputElement>(null);
  const go = (): void => {
    const raw = box.current?.value ?? text;
    const v = parseScalar(raw, doc.units);
    if (v === null || !(v > 0)) {
      setInvalid(true); // EC-DM-5/6
      setTimeout(() => setInvalid(false), 300);
      return;
    }
    onGo(v);
  };
  return (
    <span class="insp-inline pipe-action">
      <input
        ref={box}
        id={idLen}
        class={"narrow" + (invalid ? " invalid" : "")}
        value={text}
        title={title}
        disabled={disabled}
        aria-label={`${button}: length`}
        onInput={(e) => setText((e.currentTarget as HTMLInputElement).value)}
        onKeyDown={(e) => {
          e.stopPropagation(); // typing here is not a canvas shortcut
          if (e.key === "Enter") {
            e.preventDefault();
            go();
          }
        }}
      />
      <button id={idBtn} disabled={disabled} title={title} onClick={go}>
        {button}
      </button>
    </span>
  );
}

/** Pipe tool active: the size of new pipes and the look of their free ends. */
function PipeToolPanel({ tools }: { tools: ToolManager }) {
  const pref = tools.prefs.pipe;
  const sizes: number[] = PIPE_TOOL_SIZES.includes(pref.size as never) ? [...PIPE_TOOL_SIZES] : [...PIPE_TOOL_SIZES, pref.size].sort((a, b) => a - b);
  return (
    <div class="inspector pipe-block">
      <div class="insp-title">Pipe</div>
      <label class="insp-field">
        <span>Size</span>
        <select
          id="pipe-tool-size"
          value={String(pref.size)}
          title="Size of a new pipe. A pipe that starts on a fitting end takes the size of that end."
          onChange={(e) => tools.setPipePrefs({ size: Number((e.target as HTMLSelectElement).value) })}
        >
          {sizes.map((s) => (
            <option key={s} value={String(s)}>
              {s} mm
            </option>
          ))}
        </select>
      </label>
      <label class="insp-field">
        <span>Free ends</span>
        <select
          id="pipe-tool-end"
          value={pref.end}
          title="How a free end of a new pipe is drawn"
          onChange={(e) => tools.setPipePrefs({ end: (e.target as HTMLSelectElement).value as "flat" | "circle" })}
        >
          <option value="flat">{END_NAMES.flat}</option>
          <option value="circle">{END_NAMES.circle}</option>
        </select>
      </label>
      <small class="muted">
        Click the first centre point, then the next. A click on a free fitting end attaches the pipe there. Type a
        length and press Enter for an exact pipe. Right-click the pipe end for a fitting. Esc ends the run. Esc again =
        Select.
      </small>
    </div>
  );
}

/** A fitting selected: its ends, and a length + Extend for each free end. */
/**
 * 3.12.0: the size of a placed bend, tee or reducer. A change redraws the fitting at the new size where it is, and the
 * pipes on its ends take the size of their end (unless the box below is cleared).
 */
function FittingSizeBlock({ api, tools, id }: { api: AppApi; tools: ToolManager; id: string }) {
  const doc = api.store.doc;
  const info = fittingInfoOf(doc.entities[id]);
  const [withPipes, setWithPipes] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  if (!info) return null;
  const s2 = info.size2 ?? info.size;
  const list = (extra: number[], keep: (n: number) => boolean): number[] =>
    [...new Set([...FITTING_SIZE_CHOICES, ...extra])].filter(keep).sort((a, b) => a - b);
  const apply = (size: number, size2?: number) => setMsg(tools.resizeFitting(id, size, size2, withPipes));
  const sel = (elId: string, label: string, value: number, options: number[], onPick: (n: number) => void, title: string) => (
    <label class="insp-field">
      <span>{label}</span>
      <select id={elId} value={String(value)} title={title} onChange={(e) => onPick(Number((e.target as HTMLSelectElement).value))}>
        {options.map((n) => (
          <option key={n} value={String(n)}>
            {n} mm
          </option>
        ))}
      </select>
    </label>
  );
  return (
    <div class="pipe-block" id="fitting-size-block">
      <div class="insp-title">Size</div>
      {isTeeKind(info.kind) ? (
        <>
          {sel("fitting-size", "Run", info.size, list([info.size], () => true), (n) => apply(n), "The size of the run (the straight ends). An equal tee keeps its branch the same size. A smaller branch keeps its size, but never more than the run.")}
          {sel("fitting-size2", "Branch", s2, list([s2], (n) => n <= info.size), (n) => apply(info.size, n), "The size of the branch. It is never larger than the run.")}
        </>
      ) : info.kind === "reducer" ? (
        <>
          {sel("fitting-size", "Large end", info.size, list([info.size], (n) => n > s2), (n) => apply(n, s2), "The size of the large end.")}
          {sel("fitting-size2", "Small end", s2, list([s2], (n) => n < info.size), (n) => apply(info.size, n), "The size of the small end. It is smaller than the large end.")}
        </>
      ) : (
        sel("fitting-size", "Size", info.size, list([info.size], () => true), (n) => apply(n), "The size of the bend.")
      )}
      <label class="insp-check" title="On: the pipes on the ends of the fitting, and the pipes joined to them, take the size of their end.">
        <input id="fitting-with-pipes" type="checkbox" checked={withPipes} onChange={(e) => setWithPipes((e.target as HTMLInputElement).checked)} />
        <span>Pipes on its ends take the new size</span>
      </label>
      {msg && (
        <small class="muted" id="fitting-size-msg">
          {msg}
        </small>
      )}
    </div>
  );
}

/** "180", "22.5" */
const angleText = (a: number): string => Number(normAngle(a).toFixed(2)).toString();

/**
 * A lint pot (pre-strainer) selected: the chamber diameter, and each end (inlet or outlet, size, angle as drawn,
 * length from the chamber wall). Add an end, or take one away. Each change is one undo step.
 */
function LintPotBlock({ api, tools, id }: { api: AppApi; tools: ToolManager; id: string }) {
  const doc = api.store.doc;
  const [withPipes, setWithPipes] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  // a refused change: the boxes show the values the pot has again
  const [nonce, setNonce] = useState(0);
  const placed = placedLintPot(doc, id);
  if (typeof placed === "string") {
    return (
      <div class="pipe-block" id="lintpot-block">
        <div class="insp-title">Lint pot</div>
        <small class="muted" id="lintpot-msg">
          {placed}
        </small>
      </div>
    );
  }
  const spec = placed.spec;
  const go = (next: LintPotSpec, removed?: number): boolean => {
    const r = tools.editLintPot(id, { spec: next, removed, withPipes });
    setMsg(r);
    if (r) setNonce((n) => n + 1);
    return r === null;
  };
  const setEnd = (i: number, change: Partial<LintPotEnd>): boolean => go({ ...spec, ends: spec.ends.map((e, j) => (j === i ? { ...e, ...change } : e)) });
  // 3.16.1: a bend on, changed or off (off: the end has no bend value at all)
  const setBend = (i: number, bend: LintPotEnd["bend"] | null): boolean =>
    go({
      ...spec,
      ends: spec.ends.map((e, j) => (j !== i ? e : bend ? { ...e, bend } : (Object.fromEntries(Object.entries(e).filter(([k]) => k !== "bend")) as unknown as LintPotEnd)))
    });
  const ports = safePorts(doc, id);
  const largest = Math.floor(spec.diameter * LINTPOT_LIMITS.endShare);
  const elevation = spec.view === "elevation";
  const title = `${spec.make === "jagpoly" ? `JagPoly lint pot${spec.model ? ` ${spec.model}` : ""}` : "Lint pot"}${elevation ? ` · elevation${seenFromWords(spec)}` : " · plan"}`;
  return (
    <div class="pipe-block" id="lintpot-block">
      <div class="insp-title" id="lintpot-title">{title}</div>
      <label class="insp-field">
        <span>Style</span>
        <select
          key={`style-${nonce}`}
          id="lintpot-style"
          value={spec.make === "jagpoly" ? "jagpoly" : "plain"}
          title="Plain: a chamber with its ends. JagPoly: the lid ring and the Table E flanges, as the JagPoly drawings."
          onChange={(ev) => {
            const r = tools.editLintPot(id, { spec, withPipes, make: (ev.target as HTMLSelectElement).value === "jagpoly" ? "jagpoly" : null });
            setMsg(r);
            if (r) setNonce((n) => n + 1);
          }}
        >
          <option value="plain">Plain</option>
          <option value="jagpoly">JagPoly</option>
        </select>
      </label>
      <LenField
        key={`dia-${nonce}`}
        label="Chamber Ø"
        id="lintpot-diameter"
        mm={spec.diameter}
        doc={doc}
        allowNegative={false}
        title="The diameter of the chamber. The ends keep their angles and their lengths from the chamber wall."
        onCommit={(v) => void go({ ...spec, diameter: Math.round(v * 10) / 10 })}
      />
      <LenField
        key={`h-${nonce}`}
        label="Height"
        id="lintpot-height"
        mm={lintPotHeight(spec)}
        doc={doc}
        allowNegative={false}
        title="From the base to the top of the lid. The elevation shows it."
        onCommit={(v) => void go({ ...spec, height: Math.round(v * 10) / 10 })}
      />
      {elevation && (
        <label class="insp-field">
          <span>Seen from</span>
          <select
            key={`seen-${nonce}`}
            id="lintpot-seen-from"
            value={String(seenFromAngle(spec))}
            title="The side of the plan the elevation is seen from. The end angles stay the plan directions."
            onChange={(ev) => void go({ ...spec, seenFrom: Number((ev.target as HTMLSelectElement).value) })}
          >
            {[...SEEN_FROM, ...(SEEN_FROM.some((s) => s.angle === seenFromAngle(spec)) ? [] : [{ angle: seenFromAngle(spec), label: `${seenFromAngle(spec)}°` }])].map((s) => (
              <option key={s.angle} value={String(s.angle)}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {spec.ends.map((e, i) => {
        const port = ports.find((p) => p.index === i);
        const connected = !!port && !portIsFree(doc, port);
        const sizes = [...new Set([...FITTING_SIZE_CHOICES, e.size])].filter((n) => n <= largest || n === e.size).sort((a, b) => a - b);
        return (
          <div class="pipe-end-row lintpot-end" key={`${i}-${nonce}`} data-end={i}>
            <div class="pipe-end-state">
              <span class="pipe-end-name">
                End {i + 1} · {port?.label ?? e.role}
                {connected && <span class="pipe-connected"> · connected</span>}
              </span>
              <button
                type="button"
                id={`lintpot-remove-${i}`}
                disabled={spec.ends.length <= LINTPOT_LIMITS.minEnds}
                title={
                  spec.ends.length <= LINTPOT_LIMITS.minEnds
                    ? "A lint pot has 2 ends or more."
                    : connected
                      ? "Take this end away. The pipe on it stays where it is, free."
                      : "Take this end away."
                }
                onClick={() => go({ ...spec, ends: spec.ends.filter((_, j) => j !== i) }, i)}
              >
                Remove
              </button>
            </div>
            <div class="pipe-end-ctl">
              <select
                id={`lintpot-role-${i}`}
                value={e.role}
                title="Inlet or outlet. The name only: the drawing is the same."
                onChange={(ev) => setEnd(i, { role: (ev.target as HTMLSelectElement).value as LintPotEnd["role"] })}
              >
                <option value="inlet">Inlet</option>
                <option value="outlet">Outlet</option>
              </select>
              <select
                id={`lintpot-size-${i}`}
                value={String(e.size)}
                title={`The size of this end. The largest end for this chamber is ${largest} mm.`}
                onChange={(ev) => setEnd(i, { size: Number((ev.target as HTMLSelectElement).value) })}
              >
                {sizes.map((n) => (
                  <option key={n} value={String(n)}>
                    {n} mm
                  </option>
                ))}
              </select>
            </div>
            <label class="insp-field">
              <span>Angle °</span>
              <DraftInput
                id={`lintpot-angle-${i}`}
                class="narrow"
                value={angleText(e.angle)}
                title="The direction of this end on the drawing: 0 = right, 90 = down, 180 = left, 270 = up (clockwise, as the Rotation box)."
                onCommit={(raw) => {
                  const v = Number.parseFloat(raw);
                  if (!Number.isFinite(v)) return false;
                  if (!setEnd(i, { angle: normAngle(v) })) return false;
                }}
              />
            </label>
            <LenField
              label="Length"
              id={`lintpot-length-${i}`}
              mm={e.length}
              doc={doc}
              allowNegative={false}
              title={e.bend ? "From the chamber wall to the bend, on the centre line." : "From the chamber wall to the end face."}
              onCommit={(v) => void setEnd(i, { length: v })}
            />
            <label class="insp-check" title="On: the end turns after its Length, and its face looks another way. Off: the end is straight.">
              <input
                id={`lintpot-bend-${i}`}
                type="checkbox"
                checked={!!e.bend}
                onChange={(ev) => void setBend(i, (ev.target as HTMLInputElement).checked ? defaultBend(spec, e) : null)}
              />
              <span>Bend</span>
            </label>
            {e.bend && (
              <label class="insp-field">
                <span>Faces °</span>
                <DraftInput
                  id={`lintpot-bend-angle-${i}`}
                  class="narrow"
                  value={angleText(e.bend.angle)}
                  title="The direction the face of this end looks after the bend: 0 = right, 90 = down, 180 = left, 270 = up. The bend turns 10° to 135°."
                  onCommit={(raw) => {
                    const v = Number.parseFloat(raw);
                    if (!Number.isFinite(v) || !e.bend) return false;
                    if (!setBend(i, { ...e.bend, angle: normAngle(v) })) return false;
                  }}
                />
              </label>
            )}
            {e.bend && (
              <LenField
                label="After bend"
                id={`lintpot-bend-length-${i}`}
                mm={e.bend.length}
                doc={doc}
                allowNegative={false}
                title="From the bend to the end face, on the centre line."
                onCommit={(v) => void (e.bend && setBend(i, { ...e.bend, length: v }))}
              />
            )}
            <LenField
              label="Height"
              id={`lintpot-height-${i}`}
              mm={endHeight(spec, e)}
              doc={doc}
              allowNegative={false}
              title={`The height of the centre of this end above the base. From ${Math.ceil(e.size / 2)} to ${Math.floor(chamberTop(spec) - e.size / 2)} mm.`}
              onCommit={(v) => void setEnd(i, { height: Math.round(v * 10) / 10 })}
            />
          </div>
        );
      })}
      <button
        type="button"
        id="lintpot-add"
        disabled={spec.ends.length >= LINTPOT_LIMITS.maxEnds}
        title="Add an outlet in the largest space between the ends."
        onClick={() => {
          const n = withAnotherEnd(spec);
          if (typeof n === "string") setMsg(n);
          else go(n);
        }}
      >
        Add an end
      </button>
      <button
        type="button"
        id="lintpot-redraw"
        title="Draw every line of the lint pot again from its values. Use it when a line was moved by hand. The pipes stay on the ends."
        onClick={() => {
          const r = tools.editLintPot(id, { spec, withPipes: false, force: true });
          setMsg(r ?? "Drawn again from its values.");
        }}
      >
        Draw again
      </button>
      {!elevation && (
        <button
          type="button"
          id="lintpot-add-elevation"
          title="Place the elevation of this lint pot to the right of it: the same values, seen from the bottom of the plan."
          onClick={() => setMsg(tools.placeLintPotElevation(id))}
        >
          Add elevation
        </button>
      )}
      <label class="insp-check" title="On: when an end gets a new size, the pipe on it, and the pipes joined to that pipe, take the new size.">
        <input id="lintpot-with-pipes" type="checkbox" checked={withPipes} onChange={(ev) => setWithPipes((ev.target as HTMLInputElement).checked)} />
        <span>Pipes on an end take its new size</span>
      </label>
      {msg && (
        <small class="muted" id="lintpot-msg">
          {msg}
        </small>
      )}
      <small class="muted">
        Angle: 0° = right, 90° = down, 180° = left, 270° = up. Pipes on the ends stay attached. Bend: the end leaves at its
        Angle for its Length, then turns so its face looks the Faces angle.
        {elevation
          ? ` Seen from: the side of the plan you look from. The angles stay the plan directions: an end that points at that side points at you; an end that points the other way is dashed behind the chamber.`
          : " Height: the centre of the end above the base, as the elevation shows it."}
        {spec.make === "jagpoly" ? " JagPoly flanges are AS 2129 Table E." : ""}
      </small>
    </div>
  );
}

/** Pipe sizes offered for a pump end, mm. */
const PUMP_PIPE_SIZES = [32, 40, 50, 65, 80, 100, 125, 150, 200, 250, 300];

/**
 * A pump (3.15.3): the pipe size on its inlet and outlet. From 3.15.6 the pump is drawn again at the size (flange, bolt
 * circle, neck and label follow it; pipes/pumpRedraw.ts), and a plan has the outlet too (a vertical end).
 */
function PumpPipesBlock({ api, tools, id }: { api: AppApi; tools: ToolManager; id: string }) {
  const doc = api.store.doc;
  const [withPipes, setWithPipes] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  const pump = doc.entities[id]?.meta?.pump as { inletDn?: number; outletDn?: number; view?: string } | undefined;
  const ports = safePorts(doc, id);
  if (!pump || ports.length === 0) return null;
  // the catalogue size of each end (the outlet of a plan is labelled "outlet (up)")
  const flange = (label: string) => (label.startsWith("outlet") ? pump.outletDn : pump.inletDn);
  return (
    <div class="pipe-block" id="pump-pipes-block">
      <div class="insp-title">Pipe sizes</div>
      {ports.map((p) => {
        const dn = flange(p.label);
        const sizes = [...new Set([...PUMP_PIPE_SIZES, p.size, ...(dn ? [dn] : [])])].sort((a, b) => a - b);
        const label = p.label.charAt(0).toUpperCase() + p.label.slice(1);
        return (
          <label class="insp-field" key={p.index}>
            <span>
              {label}{dn ? ` (catalogue DN${dn})` : ""}
            </span>
            <select
              id={`pump-pipe-${p.index}`}
              value={String(p.size)}
              title={`The size of the ${p.label}. The flange, its bolt circle and the label take this size. Pipes drawn from this end take this size.`}
              onChange={(e) => setMsg(tools.setPortSize(id, p.index, Number((e.target as HTMLSelectElement).value), withPipes))}
            >
              {sizes.map((n) => (
                <option key={n} value={String(n)}>
                  {n} mm{n === dn ? " (catalogue)" : ""}
                </option>
              ))}
            </select>
          </label>
        );
      })}
      <label class="insp-check" title="On: the pipes on the end, and the pipes joined to them, take the new size.">
        <input id="pump-with-pipes" type="checkbox" checked={withPipes} onChange={(e) => setWithPipes((e.target as HTMLInputElement).checked)} />
        <span>Pipes on the end take the new size</span>
      </label>
      {msg && (
        <small class="muted" id="pump-pipe-msg">
          {msg}
        </small>
      )}
      {pump.view === "plan" && (
        <button
          type="button"
          id="pump-add-elevation"
          title="Place the side view (elevation) of this pump to the right of the plan, at the same sizes."
          onClick={() => setMsg(tools.placePumpElevation(id))}
        >
          Add elevation
        </button>
      )}
      <small class="muted">
        A new size draws the flange, the bolt circle and the label again at that size. A pipe joined to the side of the pump gets a rubber joint.
        {pump.view === "plan" ? " The outlet points up: a pipe from it starts as a floor penetration circle and can run any way." : ""}
      </small>
    </div>
  );
}

/** A check valve with mounting kit: its catalogue data (read only). */
function CheckValveBlock({ api, id }: { api: AppApi; id: string }) {
  const v = api.store.doc.entities[id]?.meta?.valve as
    | { maker?: string; model?: string; code?: string; D?: number; DN?: number; PN?: number; H?: number; L?: number; Z?: number; D3?: number; D4?: number; D2?: number; D2estimated?: boolean; eur?: number; source?: string; seals?: string }
    | undefined;
  if (!v) return null;
  return (
    <div class="pipe-block" id="checkvalve-block">
      <div class="insp-title">Check valve</div>
      <small class="muted">
        {v.maker} {v.model}
      </small>
      <div class="pipe-end-row">
        <div>
          Code <b>{v.code}</b> · put the colour letter (B, G, M or Y) in place of the dots
        </div>
        <div>
          D{v.D} · DN{v.DN} · PN{v.PN}
        </div>
        <div>
          Length H {v.H} mm · adaptor L {v.L} · body Z {v.Z} · flange D4 {v.D4}
        </div>
        <div>
          Bore D3 {v.D3} · body {v.D2} mm{v.D2estimated ? " (estimated)" : ""}
        </div>
        {typeof v.eur === "number" && (
          <div>
            List price € {v.eur.toFixed(2)} ({v.source})
          </div>
        )}
      </div>
      <small class="muted">The arrow shows the flow: inlet to outlet. To turn the flow round, turn the valve 180° (Q). {v.seals}.</small>
    </div>
  );
}

/**
 * A butterfly valve (3.19.0): open or closed, lever or gearbox, plan or side. Each button draws the valve again on its
 * ends (the pipes stay on). Then its data sheet values, and the parts that are drawn to proportion.
 */
function ButterflyBlock({ api, tools, id }: { api: AppApi; tools: ToolManager; id: string }) {
  const [msg, setMsg] = useState<string | null>(null);
  const info = butterflyInfoOf(api.store.doc.entities[id]);
  if (!info) return null;
  const row = butterflyRowFor(info.DN);
  const gearOk = !!row && hasOperator(row, "gearbox");
  const leverOk = !!row && hasOperator(row, "lever");
  const set = (change: Partial<ButterflySpec>) => setMsg(tools.editButterfly(id, change));
  const presets: Array<[number, string]> = [[0, "Closed"], [25, "25 %"], [50, "50 %"], [75, "75 %"], [100, "Open"]];
  const pair = <K extends keyof ButterflySpec>(key: K, label: string, options: Array<[ButterflySpec[K], string, string?]>) => (
    <label class="insp-field">
      <span>{label}</span>
      <span class="insp-inline" role="group" aria-label={label}>
        {options.map(([value, text, why]) => (
          <button
            key={String(value)}
            id={`bf-${key}-${String(value)}`}
            class={info[key] === value ? "active" : ""}
            aria-pressed={info[key] === value}
            disabled={!!why}
            title={why ?? text}
            onClick={() => set({ [key]: value } as Partial<ButterflySpec>)}
          >
            {text}
          </button>
        ))}
      </span>
    </label>
  );
  return (
    <div class="pipe-block" id="butterfly-block">
      <div class="insp-title">Butterfly valve</div>
      {/* 3.22.0: how far open, 0 to 100 % (owner, 2026-10-21: "ensure I can set the valves open and closed percentage") */}
      <label class="insp-field">
        <span title="100 % = open (the disc along the pipe); 0 % = closed (the disc across the pipe)">Open %</span>
        <input
          id="bf-pct"
          type="number"
          min={0}
          max={100}
          step={5}
          value={info.openPct}
          onChange={(e) => set({ openPct: Number((e.target as HTMLInputElement).value) })}
        />
      </label>
      <div class="colour-swatches" role="group" aria-label="Position">
        {presets.map(([v, text]) => (
          <button key={v} type="button" id={`bf-pct-${v}`} class={`wall-preset${info.openPct === v ? " active" : ""}`} aria-pressed={info.openPct === v} onClick={() => set({ openPct: v })}>
            {text}
          </button>
        ))}
      </div>
      {pair("operator", "Operator", [
        ["lever", "Lever", leverOk ? undefined : "ERA has lever valves from DN50 to DN200 only."],
        ["gearbox", "Gearbox", gearOk ? undefined : "ERA has gearbox valves from DN100 to DN400 only."]
      ])}
      {pair("view", "View", [["plan", "Plan"], ["side", "Side"]])}
      {msg && <small class="muted" id="bf-msg">{msg}</small>}
      {info.maker === "ERA" ? (
        <div class="pipe-end-row">
          <div>
            ERA · {info.item} · DN{info.DN} · {info.psi} psi
          </div>
          <div>{info.model}</div>
          <div>
            Face to face {info.L} mm · end to end with the flanges {info.length} mm
          </div>
          <div>Bolts {info.bolts}</div>
        </div>
      ) : (
        <div class="pipe-end-row">
          <div>Generic valve (placed before 3.22.0) · DN{info.DN}</div>
          <div>Click a button above to draw it again as an ERA valve.</div>
        </div>
      )}
      <small class="muted">
        A lever in line with the pipe is open; across the pipe is closed. Between them, the lever, the disc and the
        position bar of a gearbox are at the angle of the percentage. On a plan, the dashed diamond is the disc.
        {info.maker === "ERA" ? ` Drawn to proportion: ${info.estimated.join(", ")}. Source: ${info.source}.` : ""}
      </small>
    </div>
  );
}

function FittingEndsBlock({ api, tools, id }: { api: AppApi; tools: ToolManager; id: string }) {
  const doc = api.store.doc;
  const [ends, setEnds] = useState<Record<number, "flat" | "circle">>({});
  const ports = safePorts(doc, id);
  if (ports.length === 0) return null;
  return (
    <div class="pipe-block" id="pipe-ends">
      <div class="insp-title">Ends</div>
      {ports.map((p) => {
        const i = p.index;
        const free = portIsFree(doc, p);
        const endStyle = ends[i] ?? tools.prefs.pipe.end;
        return (
          <div class="pipe-end-row" key={i} data-port={i}>
            <div class="pipe-end-name">
              End {i + 1} · {p.size} mm · {p.label}
              {!free && <span class="pipe-connected"> · connected</span>}
            </div>
            {free && (
              <div class="pipe-end-ctl">
                <LengthAction
                  idLen={`pipe-extend-len-${i}`}
                  idBtn={`pipe-extend-${i}`}
                  button="Extend"
                  title={`Draw a ${p.size} mm pipe this long out of end ${i + 1}. Enter or Extend.`}
                  doc={doc}
                  onGo={(len) => tools.extendFromPort(id, i, len, endStyle)}
                />
                <select
                  id={`pipe-extend-end-${i}`}
                  value={endStyle}
                  title="How the far end of the new pipe is drawn"
                  onChange={(e) => setEnds({ ...ends, [i]: (e.target as HTMLSelectElement).value as "flat" | "circle" })}
                >
                  <option value="flat">{END_NAMES.flat}</option>
                  <option value="circle">{END_NAMES.circle}</option>
                </select>
              </div>
            )}
          </div>
        );
      })}
      <small class="muted">A pipe stays attached to its end. Move or turn the fitting and the pipes follow.</small>
    </div>
  );
}

/** "attached to the branch of Tee · 80 mm, branch 50 mm" / "free" / "free, a pipe joins here". */
function endState(doc: Doc, anchor: PipeAnchor, joined: boolean): string {
  if (anchor.kind === "free") return joined ? "free · a pipe joins here" : "free";
  const label = portsOf(doc, anchor.ownerId)[anchor.port]?.label ?? `end ${anchor.port + 1}`;
  return `attached to the ${label} of ${ownerName(doc, anchor.ownerId)}`;
}

/** A pipe selected: size, length, the two ends, detach, and a new pipe on from the free end. */
function PipeBlock({ api, tools, id }: { api: AppApi; tools: ToolManager; id: string }) {
  const doc = api.store.doc;
  const meta = pipeMetaOf(doc.entities[id]);
  if (!meta) return null;
  const ports = safePorts(doc, id);
  const length = pipeLength(doc, id);
  const bothAttached = meta.a.kind === "port" && meta.b.kind === "port";
  const sizes: number[] = PIPE_TOOL_SIZES.includes(meta.size as never) ? [...PIPE_TOOL_SIZES] : [...PIPE_TOOL_SIZES, meta.size].sort((a, b) => a - b);
  const cont = tools.pipeContinuePort(id);
  const endRow = (end: "a" | "b") => {
    const anchor = end === "a" ? meta.a : meta.b;
    const style = end === "a" ? meta.endA : meta.endB;
    const name = end.toUpperCase();
    return (
      <div class="pipe-end-row" data-end={end}>
        <label class="insp-field">
          <span>End {name}</span>
          <select
            id={`pipe-end-${end}`}
            value={style}
            title={`How end ${name} is drawn. None = joined to the next part.`}
            onChange={(e) =>
              tools.editPipe(id, end === "a" ? { endA: (e.target as HTMLSelectElement).value as PipeEndStyle } : { endB: (e.target as HTMLSelectElement).value as PipeEndStyle })
            }
          >
            {(["none", "flat", "circle", "flex"] as const).map((s) => (
              <option key={s} value={s}>
                {END_NAMES[s]}
              </option>
            ))}
          </select>
        </label>
        <div class="pipe-end-state">
          <span class="muted" id={`pipe-state-${end}`}>
            {endState(doc, anchor, !!ports[end === "a" ? 0 : 1]?.taken)}
          </span>
          {anchor.kind === "port" && (
            <button id={`pipe-detach-${end}`} title={`Free end ${name} where it is now`} onClick={() => tools.detachPipeEnd(id, end)}>
              Detach end
            </button>
          )}
        </div>
      </div>
    );
  };
  return (
    <div class="pipe-block" id="pipe-props">
      <div class="insp-title">Pipe</div>
      <label class="insp-field">
        <span>Size</span>
        <select id="pipe-size" value={String(meta.size)} onChange={(e) => tools.editPipe(id, { size: Number((e.target as HTMLSelectElement).value) })}>
          {sizes.map((s) => (
            <option key={s} value={String(s)}>
              {s} mm
            </option>
          ))}
        </select>
      </label>
      <LenField
        id="pipe-length"
        label="Length"
        mm={length}
        doc={doc}
        allowNegative={false}
        disabled={bothAttached}
        title={bothAttached ? "Both ends are attached. Detach one end to change the length." : "Length along the centre line. The free end moves."}
        onCommit={(v) => tools.editPipe(id, { length: v })}
      />
      {bothAttached && <small class="muted">Both ends are attached, so the length follows them. Detach one end to type a length.</small>}
      {endRow("a")}
      {endRow("b")}
      <div class="pipe-end-row">
        <div class="pipe-end-name">{cont === 0 ? "Continue from end A" : "Continue from end B"}</div>
        <LengthAction
          idLen="pipe-continue-len"
          idBtn="pipe-continue"
          button="Continue"
          title={cont === null ? "Both ends are attached or joined. Nothing to continue from." : "Draw a new pipe this long on from the free end, in line. Enter or Continue."}
          doc={doc}
          disabled={cont === null}
          onGo={(len) => tools.continuePipe(id, len)}
        />
      </div>
      <small class="muted">Drag a round end grip to move that end. Drop it on a free fitting end to attach it.</small>
    </div>
  );
}

/**
 * The colours of the swatch row (3.18.0). Each reads on the dark drawing and on white paper, except Standard, the
 * light grey that every library part is drawn with.
 */
export const COMPONENT_SWATCHES: ReadonlyArray<{ name: string; color: string }> = [
  { name: "Standard", color: "#e8eaf0" },
  { name: "Red", color: "#e53935" },
  { name: "Orange", color: "#fb8c00" },
  { name: "Yellow", color: "#fdd835" },
  { name: "Green", color: "#43a047" },
  { name: "Cyan", color: "#00acc1" },
  { name: "Blue", color: "#1e88e5" },
  { name: "Purple", color: "#8e24aa" },
  { name: "Grey", color: "#9e9e9e" }
];

/**
 * COLOUR OF A COMPONENT (3.18.0). Owner, 2026-10-21: "I can not set colour of a bend, pump or even a pipe." A pipe,
 * a fitting, a pump, a lint pot and any group are made of lines; this block colours every line of them (and every
 * text, with its own control). The line kind, the width and the fill stay. One undo step for each change.
 */
function ColourBlock({ api, ids }: { api: AppApi; ids: string[] }) {
  const { store } = api;
  const doc = store.doc;
  const now = componentColours(doc, ids);
  if (now.lines === 0 && now.texts === 0) return null;
  const set = (colours: { line?: string; text?: string }) => {
    const cmd = componentColourCommand(doc, ids, colours);
    if (cmd) store.dispatch(cmd);
  };
  return (
    <div class="pipe-block" id="colour-block">
      <div class="pipe-end-name">Colour</div>
      {now.lines > 0 && (
        <>
          <label class="insp-field">
            <span>Lines</span>
            <input
              id="colour-lines"
              type="color"
              aria-label="Line colour of the selection"
              title="Any colour for every line of the selection"
              value={now.line ?? "#e8eaf0"}
              onChange={(e) => set({ line: (e.target as HTMLInputElement).value })}
            />
          </label>
          <div class="colour-swatches" role="group" aria-label="Line colours">
            {COMPONENT_SWATCHES.map((s) => (
              <button
                key={s.color}
                type="button"
                class="colour-swatch"
                data-colour={s.color}
                title={s.name}
                aria-label={`${s.name} lines`}
                aria-pressed={now.line === s.color}
                style={{ background: s.color }}
                onClick={() => set({ line: s.color })}
              />
            ))}
          </div>
        </>
      )}
      {now.texts > 0 && (
        <label class="insp-field">
          <span>Text</span>
          <span class="insp-inline">
            <input
              id="colour-text"
              type="color"
              aria-label="Text colour of the selection"
              title="Colour of the text in the selection, for example the size labels of a pump"
              value={now.text ?? "#e8eaf0"}
              onChange={(e) => set({ text: (e.target as HTMLInputElement).value })}
            />
            {now.lines > 0 && now.line && now.text !== now.line && (
              <button type="button" id="colour-text-same" title="Give the text the colour of the lines" onClick={() => set({ text: now.line! })}>
                Same as lines
              </button>
            )}
          </span>
        </label>
      )}
      <small class="muted">
        Every line of the selection takes the colour. The line kind, the width and the fill stay. A new size or a redraw
        keeps the colour.
      </small>
      <LineWidthRow api={api} ids={ids} />
    </div>
  );
}

/** 3.26.0: the screen widths (px) of the Line width buttons. 1.5 px is the width of the library parts. */
export const COMPONENT_WIDTHS: ReadonlyArray<{ name: string; px: number }> = [
  { name: "Standard", px: 1.5 },
  { name: "Medium", px: 3 },
  { name: "Thick", px: 5 },
  { name: "Extra thick", px: 8 }
];

/**
 * LINE WIDTH OF COMPONENTS (3.26.0). Owner, 2026-10-23: "I need a tool to set the line width of all selected components.
 * I am overlaying pipes and I want the ones on a lower layer to be thicker so they can be seen below the others that
 * are thinner." Every line of the selection takes the width, except the centre lines inside a component. One undo step.
 */
function LineWidthRow({ api, ids }: { api: AppApi; ids: string[] }) {
  const { store } = api;
  const doc = store.doc;
  const now = componentWidth(doc, ids);
  if (now.lines === 0) return null;
  const set = (px: number) => {
    const cmd = componentWidthCommand(doc, ids, px);
    if (cmd) store.dispatch(cmd);
  };
  return (
    <>
      <label class="insp-field">
        <span>Line width</span>
        <span class="insp-inline">
          <input
            key={`w-${now.px ?? "mixed"}`}
            id="component-width"
            type="number"
            min="0.25"
            max="40"
            step="0.25"
            aria-label="Line width of the selection, in screen pixels"
            title="Line width in screen pixels: the same at every zoom. The library parts are 1.5 px."
            placeholder="mixed"
            defaultValue={now.px === null ? "" : String(now.px)}
            onChange={(e) => {
              const v = Number((e.target as HTMLInputElement).value);
              if (v > 0) set(Math.min(40, v));
            }}
          />
          <span class="muted">px</span>
        </span>
      </label>
      <div class="colour-swatches" role="group" aria-label="Line widths">
        {COMPONENT_WIDTHS.map((w) => (
          <button
            key={w.px}
            type="button"
            class="width-choice"
            data-width={w.px}
            title={`${w.name}: ${w.px} px`}
            aria-label={`${w.name} lines, ${w.px} px`}
            aria-pressed={now.px === w.px}
            onClick={() => set(w.px)}
          >
            {w.px}
          </button>
        ))}
      </div>
      <small class="muted">
        Every line of the selection takes the width, except the centre lines inside a component. To see a pipe below
        another pipe, give the lower pipe the larger width.
      </small>
    </>
  );
}

// ---- walls (3.20.0) -------------------------------------------------------------------------

const FILL_CHOICES: Array<[FillMode, string]> = [["hatch", "Hatch"], ["crosshatch", "Crosshatch"], ["dots", "Dots"], ["solid", "Solid"], ["none", "None"]];
const JUSTIFY_CHOICES: Array<[WallJustify, string]> = [["centre", "Centre"], ["left", "Left face"], ["right", "Right face"]];

/** Wall tool on: walls or a room, the thickness (with the common ones), the line you draw, the fill. */
function WallToolPanel({ api, tools }: { api: AppApi; tools: ToolManager }) {
  const pref = tools.prefs.wall;
  const doc = api.store.doc;
  return (
    <div class="inspector pipe-block" id="wall-tool">
      <div class="insp-title">Wall</div>
      <label class="insp-field">
        <span>Draw</span>
        <span class="insp-inline" role="group" aria-label="Draw">
          <button id="wall-mode-walls" class={pref.mode === "walls" ? "active" : ""} aria-pressed={pref.mode === "walls"} title="Click the corners of the walls, one after the other" onClick={() => tools.setWallPrefs({ mode: "walls" })}>
            Walls
          </button>
          <button id="wall-mode-room" class={pref.mode === "room" ? "active" : ""} aria-pressed={pref.mode === "room"} title="Click two opposite corners, or type width,depth: four walls" onClick={() => tools.setWallPrefs({ mode: "room" })}>
            Room
          </button>
        </span>
      </label>
      <LenField label="Thickness" id="wall-thickness" mm={pref.thickness} doc={doc} allowNegative={false} onCommit={(v) => tools.setWallPrefs({ thickness: v })} />
      <div class="colour-swatches" role="group" aria-label="Common thicknesses">
        {WALL_THICKNESSES.map((t) => (
          <button key={t} type="button" class={`wall-preset${pref.thickness === t ? " active" : ""}`} data-thickness={t} aria-pressed={pref.thickness === t} onClick={() => tools.setWallPrefs({ thickness: t })}>
            {t}
          </button>
        ))}
      </div>
      <label class="insp-field">
        <span title="The line you draw is the centre of the wall, or its left or right face (as you go along the line)">The line is</span>
        <select id="wall-justify" value={pref.justify} onChange={(e) => tools.setWallPrefs({ justify: (e.target as HTMLSelectElement).value as WallJustify })}>
          {JUSTIFY_CHOICES.map(([v, t]) => (
            <option key={v} value={v}>
              {t}
            </option>
          ))}
        </select>
      </label>
      <label class="insp-field">
        <span>Fill</span>
        <select id="wall-fill" value={pref.fill} onChange={(e) => tools.setWallPrefs({ fill: (e.target as HTMLSelectElement).value as FillMode })}>
          {FILL_CHOICES.map(([v, t]) => (
            <option key={v} value={v}>
              {t}
            </option>
          ))}
        </select>
      </label>
      <small class="muted">
        {pref.mode === "room"
          ? "Click one corner, then the opposite corner. Or click one corner and type width,depth (for example 4000,3000) and press Enter."
          : "Click the corners. Type a length (for example 3000) and press Enter. Shift keeps a wall square. Click the first point to close a room. Enter or right-click ends the walls."}{" "}
        Click on another wall to join it: at its free end the walls meet in a mitre, on its length they make a T. Left and right are as you go along the line you draw. To add a door, select the wall and right-click it where the door goes.
      </small>
    </div>
  );
}

/**
 * 3.21.0: the door picked inside the selected wall. Owner, 2026-10-21: "the inspector should allow you to set
 * properties for that door when it's selected inside the wall". Kind, opening, place (from either corner), side, hinge.
 */
function DoorBlock({ api, tools, wallId, doorId }: { api: AppApi; tools: ToolManager; wallId: string; doorId: string }) {
  const [msg, setMsg] = useState<string | null>(null);
  const doc = api.store.doc;
  const w = wallMetaOf(doc.entities[wallId]);
  const spine = spineOf(doc, wallId);
  const geo = w && spine ? wallGeometry(spine.points, spine.closed, w) : null;
  const d = w?.doors.find((x) => x.id === doorId);
  const span = geo && d ? doorSpan(geo, d) : null;
  if (!w || !d || !span) return null;
  const say = (r: string | null) => setMsg(r);
  const kinds: DoorKind[] = ["single", "double", "roller"];
  const len = span.seg.len;
  return (
    <div class="pipe-block" id="door-block" data-door={d.id}>
      <div class="insp-title" style={{ textTransform: "none" }}>
        {DOOR_WORDS[d.kind]} · wall {d.seg + 1}
      </div>
      <label class="insp-field">
        <span>Kind</span>
        <select id="door-sel-kind" value={d.kind} onChange={(e) => say(tools.editDoor(wallId, d.id, { kind: (e.target as HTMLSelectElement).value as DoorKind }))}>
          {kinds.map((k) => (
            <option key={k} value={k}>
              {DOOR_WORDS[k]}
            </option>
          ))}
        </select>
      </label>
      <LenField label="Opening" id="door-sel-width" mm={d.width} doc={doc} allowNegative={false} onCommit={(v) => say(tools.editDoor(wallId, d.id, { width: v }))} />
      <LenField label="From the corner" id="door-sel-from" mm={span.ua} doc={doc} allowNegative={false} title={`From the start of wall ${d.seg + 1} to the near side of the opening`} onCommit={(v) => say(tools.editDoor(wallId, d.id, { at: v + d.width / 2 }))} />
      <LenField label="To the far corner" id="door-sel-to" mm={len - span.ub} doc={doc} allowNegative={false} title={`From the far side of the opening to the end of wall ${d.seg + 1}`} onCommit={(v) => say(tools.editDoor(wallId, d.id, { at: len - v - d.width / 2 }))} />
      <div class="pipe-end-ctl">
        <button id="door-sel-side" title={d.kind === "roller" ? "Put the drum on the other side of the wall" : "Open the door to the other side of the wall"} onClick={() => say(tools.editDoor(wallId, d.id, { side: d.side === 1 ? -1 : 1 }))}>
          {d.kind === "roller" ? "Drum to the other side" : "Open to the other side"}
        </button>
        {d.kind === "single" && (
          <button id="door-sel-hinge" title="Hinges on the other jamb" onClick={() => say(tools.editDoor(wallId, d.id, { hinge: d.hinge === "start" ? "end" : "start" }))}>
            Other jamb
          </button>
        )}
      </div>
      <div class="pipe-end-ctl">
        <button id="door-sel-remove" class="danger" title="Take the door away: the wall is whole there again" onClick={() => say(tools.removeDoor(wallId, d.id))}>
          Remove the door
        </button>
        <button id="door-sel-done" title="Go back to the wall (Esc)" onClick={() => tools.selectDoor(null)}>
          Back to the wall
        </button>
      </div>
      {msg && (
        <small class="muted" id="door-msg">
          {msg}
        </small>
      )}
      <small class="muted">Drag the door along the wall to move it. Delete takes the door away. Esc goes back to the wall.</small>
    </div>
  );
}

/** A selected wall run: thickness, line, fill, the length of each wall, and its doors. */
function WallBlock({ api, tools, id }: { api: AppApi; tools: ToolManager; id: string }) {
  const [msg, setMsg] = useState<string | null>(null);
  const doc = api.store.doc;
  const w = wallMetaOf(doc.entities[id]);
  const spine = spineOf(doc, id);
  const geo = w && spine ? wallGeometry(spine.points, spine.closed, w) : null;
  if (!w || !geo) return null;
  const say = (r: string | null) => setMsg(r);
  const kinds: DoorKind[] = ["single", "double", "roller"];
  // 3.23.0: what each end of the run joins (found from the drawing, so it changes when a wall moves)
  const joins = geo.closed ? {} : (wallNetwork(doc).get(id)?.joins ?? {});
  const joinWords = (j: EndJoin | undefined) => (!j ? "free" : j.kind === "mitre" ? "meets the end of a wall" : "joins a wall (a T)");
  return (
    <div class="pipe-block" id="wall-block">
      <div class="insp-title">Wall</div>
      <LenField label="Thickness" id="wall-sel-thickness" mm={w.thickness} doc={doc} allowNegative={false} onCommit={(v) => say(tools.editWall(id, { thickness: v }))} />
      <div class="colour-swatches" role="group" aria-label="Common thicknesses">
        {WALL_THICKNESSES.map((t) => (
          <button key={t} type="button" class={`wall-preset${w.thickness === t ? " active" : ""}`} data-thickness={t} aria-pressed={w.thickness === t} onClick={() => say(tools.editWall(id, { thickness: t }))}>
            {t}
          </button>
        ))}
      </div>
      <label class="insp-field">
        <span title="The line you draw is the centre of the wall, or its left or right face (as you go along the line)">The line is</span>
        <select id="wall-sel-justify" value={w.justify} onChange={(e) => say(tools.editWall(id, { justify: (e.target as HTMLSelectElement).value as WallJustify }))}>
          {JUSTIFY_CHOICES.map(([v, t]) => (
            <option key={v} value={v}>
              {t}
            </option>
          ))}
        </select>
      </label>
      <label class="insp-field">
        <span>Fill</span>
        <span class="insp-inline">
          <select id="wall-sel-fill" value={w.fill.mode} onChange={(e) => say(tools.editWall(id, { fill: { ...w.fill, mode: (e.target as HTMLSelectElement).value as FillMode } }))}>
            {FILL_CHOICES.map(([v, t]) => (
              <option key={v} value={v}>
                {t}
              </option>
            ))}
          </select>
          <input type="color" id="wall-sel-fill-colour" aria-label="Fill colour" value={w.fill.color} onChange={(e) => say(tools.editWall(id, { fill: { ...w.fill, color: (e.target as HTMLInputElement).value } }))} />
        </span>
      </label>
      <div class="pipe-end-name">{geo.closed ? "Walls of the room" : "Walls"}</div>
      {!geo.closed && (
        <small class="muted" id="wall-joins">
          Start: {joinWords(joins.start)} · End: {joinWords(joins.end)}
        </small>
      )}
      {geo.segs.map((s) => (
        <LenField key={`len-${s.index}-${s.len}`} label={`Wall ${s.index + 1}`} id={`wall-len-${s.index}`} mm={s.len} doc={doc} allowNegative={false} onCommit={(v) => say(tools.setWallLength(id, s.index, v))} />
      ))}
      <div class="pipe-end-name">Doors · {w.doors.length}</div>
      {w.doors.map((d, n) => (
        <div class={`pipe-end-row${tools.selectedDoor?.doorId === d.id ? " picked" : ""}`} key={d.id} data-door={d.id}>
          <div class="pipe-end-ctl">
            <select id={`door-kind-${n}`} value={d.kind} aria-label="Kind of door" onChange={(e) => say(tools.editDoor(id, d.id, { kind: (e.target as HTMLSelectElement).value as DoorKind }))}>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {DOOR_WORDS[k]}
                </option>
              ))}
            </select>
            <span class="muted">in wall {d.seg + 1}</span>
            <button id={`door-pick-${n}`} title="Select this door: it is marked in the drawing and its settings show at the top" onClick={() => tools.selectDoor({ wallId: id, doorId: d.id })}>
              Select
            </button>
          </div>
          <LenField label="Opening" id={`door-width-${n}`} mm={d.width} doc={doc} allowNegative={false} onCommit={(v) => say(tools.editDoor(id, d.id, { width: v }))} />
          <LenField label="From the corner" id={`door-from-${n}`} mm={d.at - d.width / 2} doc={doc} allowNegative={false} title={`From the start of wall ${d.seg + 1} to the near side of the opening`} onCommit={(v) => say(tools.editDoor(id, d.id, { at: v + d.width / 2 }))} />
          <div class="pipe-end-ctl">
            <button id={`door-side-${n}`} title={d.kind === "roller" ? "Put the drum on the other side of the wall" : "Open the door to the other side of the wall"} onClick={() => say(tools.editDoor(id, d.id, { side: d.side === 1 ? -1 : 1 }))}>
              Other side
            </button>
            {d.kind === "single" && (
              <button id={`door-hinge-${n}`} title="Hinges on the other jamb" onClick={() => say(tools.editDoor(id, d.id, { hinge: d.hinge === "start" ? "end" : "start" }))}>
                Other jamb
              </button>
            )}
            <button id={`door-remove-${n}`} class="danger" title="Take the door away: the wall is whole there again" onClick={() => say(tools.removeDoor(id, d.id))}>
              Remove
            </button>
          </div>
        </div>
      ))}
      {msg && (
        <small class="muted" id="wall-msg">
          {msg}
        </small>
      )}
      <small class="muted">
        To add a door, right-click the wall where the door goes. Click a door to select it; drag it to move it along the wall. Drag the grip at an end to make the wall
        longer or shorter, or onto another wall to join it. Drag the grip at a corner to move it. Right-click the wall to split it, to break it at a corner, or to split it
        where it meets another wall.
      </small>
    </div>
  );
}

export function Inspector({ api, tools }: { api: AppApi; tools: ToolManager }) {
  const { store } = api;
  const doc = store.doc;
  const sel = [...tools.selection.all];

  if (tools.activeToolId === "perspective") return <PerspectivePanel tools={tools} />;
  if (tools.activeToolId === "pipe") return <PipeToolPanel tools={tools} />;
  if (tools.activeToolId === "wall") return <WallToolPanel api={api} tools={tools} />;
  if (tools.activeToolId === "marker" && tools.markerKind === "highlight" && sel.length === 0) {
    return <HighlighterBlock tools={tools} />;
  }

  const applyStyle = (ids: string[], style: Style) => {
    const cmd = applyStyleCommand(doc, ids, style);
    if (cmd) store.dispatch(cmd);
  };

  // ---- empty: document defaults --------------------------------------------
  if (sel.length === 0) {
    const setProps = (after: Partial<Pick<Doc, "units" | "precision" | "defaultStyle" | "canvasGrid">>) => {
      const before: Record<string, unknown> = {};
      for (const k of Object.keys(after)) before[k] = structuredClone((doc as unknown as Record<string, unknown>)[k]);
      store.dispatch({ kind: "setDocProps", before, after });
    };
    return (
      <div class="inspector">
        <div class="insp-title">Document defaults</div>
        <label class="insp-field">
          <span>Units</span>
          <span class="insp-inline">
            <select value={doc.units} onChange={(e) => setProps({ units: (e.target as HTMLSelectElement).value as Units })}>
              <option value="mm">mm</option>
              <option value="cm">cm</option>
              <option value="m">m</option>
              <option value="in">in</option>
              <option value="ftin">ft-in</option>
            </select>
            <select
              value={doc.precision}
              title="Decimals"
              onChange={(e) => setProps({ precision: Number((e.target as HTMLSelectElement).value) })}
            >
              {[0, 1, 2, 3, 4].map((p) => (
                <option key={p} value={p}>
                  {p} dp
                </option>
              ))}
            </select>
          </span>
        </label>
        <StyleEditor style={doc.defaultStyle} onChange={(s) => setProps({ defaultStyle: s })} />
        <label class="insp-field">
          <span>Canvas grid</span>
          <span class="insp-inline">
            <label class="insp-check">
              <input
                type="checkbox"
                checked={doc.canvasGrid.visible}
                onChange={(e) =>
                  setProps({ canvasGrid: { ...doc.canvasGrid, visible: (e.target as HTMLInputElement).checked } })
                }
              />
              show
            </label>
            <label class="insp-check">
              <input
                type="checkbox"
                checked={doc.canvasGrid.snap}
                onChange={(e) =>
                  setProps({ canvasGrid: { ...doc.canvasGrid, snap: (e.target as HTMLInputElement).checked } })
                }
              />
              snap
            </label>
          </span>
        </label>
        <small class="muted">New objects use these defaults. Select an object to edit it.</small>
      </div>
    );
  }

  // ---- single entity ---------------------------------------------------------
  if (sel.length === 1) {
    const id = sel[0]!;
    const entity = doc.entities[id];
    if (!entity) return null;
    const isPipeSel = looksLikePipe(entity);
    const isFittingSel = !isPipeSel && looksLikeFitting(entity);
    const wallSel = wallMetaOf(entity);
    // Lines, boxes and circles report their drawn size, not the stroke around it:
    // a 3000 wide box reads 3000, not 3000.4.
    const b = geometryBounds(entity as never) ?? entityBounds(entity, doc);
    const patch = (before: Record<string, unknown>, after: Record<string, unknown>) =>
      store.dispatch({ kind: "updateEntities", patches: [{ id, before, after }] });
    const moveTo = (dx: number, dy: number) => {
      if (Math.abs(dx) > 1e-9 || Math.abs(dy) > 1e-9) store.dispatch(moveCommand(doc, [id], { x: dx, y: dy }));
    };
    return (
      <div class="inspector">
        <div class="insp-title" style={isFittingSel || wallSel ? { textTransform: "none" } : undefined}>
          {isPipeSel ? "pipe" : isFittingSel ? ownerName(doc, id) : wallSel ? `${wallSel.closed ? "Room" : "Wall"} · ${Math.round(wallSel.thickness)} mm` : entity.type}
          {entity.locked ? " · locked" : ""}
        </div>
        {wallSel && !entity.locked && tools.selectedDoor?.wallId === id && <DoorBlock key={`door-${tools.selectedDoor.doorId}`} api={api} tools={tools} wallId={id} doorId={tools.selectedDoor.doorId} />}
        <label class="insp-field">
          <span>Layer</span>
          <select
            value={entity.layerId}
            onChange={(e) => tools.moveSelectionToLayer((e.target as HTMLSelectElement).value)}
          >
            {doc.layers.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </label>
        <LenField label="X" mm={b.minX} doc={doc} onCommit={(v) => moveTo(v - b.minX, 0)} />
        <LenField label="Y" mm={b.minY} doc={doc} onCommit={(v) => moveTo(0, v - b.minY)} />
        {isPipeSel && !entity.locked && <PipeBlock key={id} api={api} tools={tools} id={id} />}
        {wallSel && !entity.locked && <WallBlock key={`wall-${id}`} api={api} tools={tools} id={id} />}
        {isFittingSel && !entity.locked && fittingInfoOf(entity) && <FittingSizeBlock key={`size-${id}`} api={api} tools={tools} id={id} />}
        {isFittingSel && !entity.locked && isLintPot(entity) && <LintPotBlock key={`lintpot-${id}`} api={api} tools={tools} id={id} />}
        {isFittingSel && !!entity.meta?.valve && <CheckValveBlock key={`valve-${id}`} api={api} id={id} />}
        {isFittingSel && !entity.locked && !!butterflyInfoOf(entity) && <ButterflyBlock key={`butterfly-${id}`} api={api} tools={tools} id={id} />}
        {isFittingSel && !entity.locked && !!entity.meta?.pump && <PumpPipesBlock key={`pump-${id}`} api={api} tools={tools} id={id} />}
        {isFittingSel && !entity.locked && <FittingEndsBlock key={id} api={api} tools={tools} id={id} />}
        {!entity.locked &&
          !isPipeSel &&
          (entity.type === "text" ||
            entity.type === "image" ||
            entity.type === "grid" ||
            entity.type === "group" ||
            entity.type === "arc" ||
            (entity.type === "polyline" && entity.points.length > 2)) && <RotationField api={api} id={id} />}
        {!entity.locked && !isPipeSel && entity.type !== "dimension" && entity.type !== "label" && <PivotField api={api} tools={tools} id={id} />}
        {entity.type === "circle" && (
          <LenField
            label="Radius"
            mm={entity.r}
            doc={doc}
            allowNegative={false}
            onCommit={(v) => patch({ r: entity.r }, { r: v })}
          />
        )}
        {entity.type === "arc" && (
          <LenField
            label="Radius"
            mm={entity.r}
            doc={doc}
            allowNegative={false}
            onCommit={(v) => patch({ r: entity.r }, { r: v })}
          />
        )}
        {entity.type === "polyline" && entity.points.length === 2 && !entity.locked && (
          <>
            <LenField
              label="Length"
              mm={dist(entity.points[0]!, entity.points[1]!)}
              doc={doc}
              allowNegative={false}
              onCommit={(len) => {
                const [a, b] = entity.points as [typeof entity.points[0], typeof entity.points[0]];
                const d = dir(angleOf(sub(b!, a!)));
                patch({ points: entity.points }, { points: [a, { x: a!.x + d.x * len, y: a!.y + d.y * len }] });
              }}
            />
            <label class="insp-field">
              <span>Angle °</span>
              <DraftInput
                value={angleOf(sub(entity.points[1]!, entity.points[0]!)).toFixed(1)}
                title="0 points right, 90 points down, as the drawing readout shows"
                onCommit={(raw) => {
                  const v = Number.parseFloat(raw);
                  if (!Number.isFinite(v)) return false;
                  const [a, b] = entity.points as [typeof entity.points[0], typeof entity.points[0]];
                  const len = dist(a!, b!);
                  const d = dir(v);
                  patch({ points: entity.points }, { points: [a, { x: a!.x + d.x * len, y: a!.y + d.y * len }] });
                }}
              />
            </label>
          </>
        )}
        {entity.type === "polyline" && entity.points.length > 2 && !entity.locked && (
          <>
            <LenField
              label="Width"
              mm={b.maxX - b.minX}
              doc={doc}
              allowNegative={false}
              onCommit={(w) => patch({ points: entity.points }, { points: resizePoints(entity.points, w, null) })}
            />
            <LenField
              label="Height"
              mm={b.maxY - b.minY}
              doc={doc}
              allowNegative={false}
              onCommit={(h) => patch({ points: entity.points }, { points: resizePoints(entity.points, null, h) })}
            />
          </>
        )}
        {entity.type === "polyline" && (
          <label class="insp-field">
            <span>{entity.points.length} points</span>
            <label class="insp-check">
              <input
                type="checkbox"
                checked={entity.closed}
                disabled={entity.points.length < 3}
                onChange={() => patch({ closed: entity.closed }, { closed: !entity.closed })}
              />
              closed
            </label>
          </label>
        )}
        {entity.type === "group" && !isPipeSel && !wallSel && (
          <>
            <small class="muted">{entity.children.length} objects grouped</small>
            <div class="insp-actions">
              <button onClick={() => tools.ungroupSelection()} title="Ctrl+Shift+G">
                Ungroup
              </button>
            </div>
          </>
        )}
        {entity.type === "label" && (
          <label class="insp-field">
            <span>Arrow</span>
            <select
              value={entity.arrow}
              onChange={(e) =>
                patch({ arrow: entity.arrow }, { arrow: (e.target as HTMLSelectElement).value as typeof entity.arrow })
              }
            >
              <option value="arrow">Arrow</option>
              <option value="dot">Dot</option>
              <option value="none">None</option>
            </select>
          </label>
        )}
        {(entity.type === "text" || entity.type === "label") && !entity.locked && (
          <TextStyleBlock
            ts={entity.textStyle}
            doc={doc}
            onChange={(next) => patch({ textStyle: entity.textStyle }, { textStyle: next })}
          />
        )}
        {entity.type === "dimension" && (
          <>
            {entity.kind === "linear" && !entity.locked && (
              <label class="insp-field">
                <span>Measures</span>
                <select
                  id="insp-dim-orient"
                  value={entity.orient}
                  title="Aligned = the straight distance between the two points. Horizontal or Vertical = only the sideways or up-down part."
                  onChange={(e) =>
                    patch({ orient: entity.orient }, { orient: (e.target as HTMLSelectElement).value as typeof entity.orient })
                  }
                >
                  <option value="aligned">Aligned (straight)</option>
                  <option value="h">Horizontal</option>
                  <option value="v">Vertical</option>
                </select>
              </label>
            )}
            <small class="muted">Drag the end grips to move the points, and the middle grip to move the dimension line.</small>
            <label class="insp-field">
              <span>Decimals</span>
              <select
                value={entity.precisionOverride ?? -1}
                onChange={(e) => {
                  const v = Number((e.target as HTMLSelectElement).value);
                  patch(
                    { precisionOverride: entity.precisionOverride },
                    { precisionOverride: v < 0 ? undefined : v }
                  );
                }}
              >
                <option value={-1}>document</option>
                {[0, 1, 2, 3, 4].map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
            {(["prefix", "suffix", "textOverride"] as const).map((field) => (
              <label class="insp-field" key={field}>
                <span>{field === "textOverride" ? "Override" : field}</span>
                <DraftInput
                  value={entity[field] ?? ""}
                  onCommit={(v) => {
                    patch({ [field]: entity[field] }, { [field]: v || undefined });
                  }}
                />
              </label>
            ))}
            {entity.meta?.orphan === true && (
              <small class="muted" style="color: var(--danger)">
                Reference was deleted — dimension no longer follows geometry
              </small>
            )}
            <TextStyleBlock
              ts={doc.dimStyle.textStyle}
              doc={doc}
              label="Dim text"
              onChange={(next) =>
                store.dispatch({
                  kind: "setDocProps",
                  before: { dimStyle: structuredClone(doc.dimStyle) },
                  after: { dimStyle: { ...structuredClone(doc.dimStyle), textStyle: next } }
                })
              }
            />
            <small class="muted">Dimension text style is document-wide (all dimensions).</small>
          </>
        )}
        {entity.type === "grid" && (
          <>
            <label class="insp-field">
              <span>Cols × Rows</span>
              <span class="insp-inline">
                <DraftInput
                  class="narrow"
                  type="number"
                  min="1"
                  max="1000"
                  value={String(entity.cols)}
                  onCommit={(raw) => {
                    const v = Number(raw);
                    if (!(v >= 1 && v <= 1000)) return false;
                    patch({ cols: entity.cols }, { cols: Math.round(v) });
                  }}
                />
                ×
                <DraftInput
                  class="narrow"
                  type="number"
                  min="1"
                  max="1000"
                  value={String(entity.rows)}
                  onCommit={(raw) => {
                    const v = Number(raw);
                    if (!(v >= 1 && v <= 1000)) return false;
                    patch({ rows: entity.rows }, { rows: Math.round(v) });
                  }}
                />
              </span>
            </label>
            <LenField
              label="Cell W"
              mm={entity.cellW}
              doc={doc}
              allowNegative={false}
              onCommit={(v) => patch({ cellW: entity.cellW }, { cellW: v })}
            />
            <LenField
              label="Cell H"
              mm={entity.cellH}
              doc={doc}
              allowNegative={false}
              onCommit={(v) => patch({ cellH: entity.cellH }, { cellH: v })}
            />
            <label class="insp-field">
              <span>Labels</span>
              <span class="insp-inline">
                <label class="insp-check">
                  <input
                    type="checkbox"
                    checked={entity.labels.cols}
                    onChange={() =>
                      patch({ labels: entity.labels }, { labels: { ...entity.labels, cols: !entity.labels.cols } })
                    }
                  />
                  cols
                </label>
                <label class="insp-check">
                  <input
                    type="checkbox"
                    checked={entity.labels.rows}
                    onChange={() =>
                      patch({ labels: entity.labels }, { labels: { ...entity.labels, rows: !entity.labels.rows } })
                    }
                  />
                  rows
                </label>
              </span>
            </label>
            <label class="insp-field">
              <span>Label style</span>
              <span class="insp-inline">
                <select
                  value={entity.labels.colMode}
                  title="Column labels"
                  onChange={(e) =>
                    patch(
                      { labels: entity.labels },
                      {
                        labels: {
                          ...entity.labels,
                          colMode: (e.target as HTMLSelectElement).value as "letters" | "numbers"
                        }
                      }
                    )
                  }
                >
                  <option value="letters">cols A B C</option>
                  <option value="numbers">cols 1 2 3</option>
                </select>
                <select
                  value={entity.labels.rowMode}
                  title="Row labels"
                  onChange={(e) =>
                    patch(
                      { labels: entity.labels },
                      {
                        labels: {
                          ...entity.labels,
                          rowMode: (e.target as HTMLSelectElement).value as "letters" | "numbers"
                        }
                      }
                    )
                  }
                >
                  <option value="letters">rows A B C</option>
                  <option value="numbers">rows 1 2 3</option>
                </select>
              </span>
            </label>
            <label class="insp-field">
              <span>Label text</span>
              <span class="insp-inline">
                <input
                  type="color"
                  value={entity.labels.textStyle.color}
                  title="Legend color"
                  aria-label="Legend color"
                  onChange={(e) =>
                    patch(
                      { labels: entity.labels },
                      {
                        labels: {
                          ...entity.labels,
                          textStyle: { ...entity.labels.textStyle, color: (e.target as HTMLInputElement).value }
                        }
                      }
                    )
                  }
                />
                <input
                  class="narrow"
                  type="number"
                  min="0.5"
                  max="100"
                  step="0.5"
                  value={entity.labels.textStyle.size}
                  title="Legend size (mm)"
                  onKeyDown={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const v = Number((e.target as HTMLInputElement).value);
                    if (v >= 0.5 && v <= 100) {
                      patch(
                        { labels: entity.labels },
                        { labels: { ...entity.labels, textStyle: { ...entity.labels.textStyle, size: v } } }
                      );
                    }
                  }}
                />
                <label class="insp-check" title="Bold">
                  <input
                    type="checkbox"
                    checked={entity.labels.textStyle.bold}
                    onChange={() =>
                      patch(
                        { labels: entity.labels },
                        {
                          labels: {
                            ...entity.labels,
                            textStyle: { ...entity.labels.textStyle, bold: !entity.labels.textStyle.bold }
                          }
                        }
                      )
                    }
                  />
                  B
                </label>
              </span>
            </label>
            <label class="insp-field">
              <span>Label font</span>
              <select
                value={entity.labels.textStyle.font}
                onChange={(e) =>
                  patch(
                    { labels: entity.labels },
                    {
                      labels: {
                        ...entity.labels,
                        textStyle: {
                          ...entity.labels.textStyle,
                          font: (e.target as HTMLSelectElement).value as typeof entity.labels.textStyle.font
                        }
                      }
                    }
                  )
                }
              >
                <option value="Inter">Inter</option>
                <option value="Caveat">Caveat (handwritten)</option>
                <option value="JetBrains Mono">JetBrains Mono</option>
              </select>
            </label>
            <label class="insp-field">
              <span>Lock cells</span>
              <input
                type="checkbox"
                checked={entity.lockAspect}
                title="Resize keeps cell size (recomputes counts) vs stretches cells"
                onChange={() => patch({ lockAspect: entity.lockAspect }, { lockAspect: !entity.lockAspect })}
              />
            </label>
            <label class="insp-field">
              <span>Line color</span>
              <input
                type="color"
                value={entity.vStyle.color}
                aria-label="Grid line color"
                onChange={(e) => {
                  const color = (e.target as HTMLInputElement).value;
                  patch(
                    { vStyle: entity.vStyle, hStyle: entity.hStyle },
                    { vStyle: { ...entity.vStyle, color }, hStyle: { ...entity.hStyle, color } }
                  );
                }}
              />
            </label>
          </>
        )}
        {entity.type === "image" && (
          <>
            <LenField
              label="Width"
              mm={entity.wMm}
              doc={doc}
              allowNegative={false}
              onCommit={(v) =>
                patch({ wMm: entity.wMm, hMm: entity.hMm }, { wMm: v, hMm: (entity.hMm * v) / entity.wMm })
              }
            />
            <LenField
              label="Height"
              mm={entity.hMm}
              doc={doc}
              allowNegative={false}
              onCommit={(v) =>
                patch({ wMm: entity.wMm, hMm: entity.hMm }, { wMm: (entity.wMm * v) / entity.hMm, hMm: v })
              }
            />
            <label class="insp-field">
              <span>Rotation</span>
              <input
                type="number"
                step="1"
                value={Math.round(entity.rotationDeg * 10) / 10}
                onKeyDown={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const v = Number((e.target as HTMLInputElement).value);
                  if (Number.isFinite(v)) patch({ rotationDeg: entity.rotationDeg }, { rotationDeg: v });
                }}
              />
            </label>
            <label class="insp-field">
              <span>Opacity</span>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(entity.opacity * 100)}
                onChange={(e) =>
                  patch({ opacity: entity.opacity }, { opacity: Number((e.target as HTMLInputElement).value) / 100 })
                }
              />
            </label>
            <label class="insp-field">
              <span>Grayscale</span>
              <input
                type="checkbox"
                checked={entity.grayscale}
                onChange={() => patch({ grayscale: entity.grayscale }, { grayscale: !entity.grayscale })}
              />
            </label>
            <label class="insp-field">
              <span>Brightness</span>
              <input
                type="range"
                min="-100"
                max="100"
                value={Math.round(entity.brightness * 100)}
                onChange={(e) =>
                  patch(
                    { brightness: entity.brightness },
                    { brightness: Number((e.target as HTMLInputElement).value) / 100 }
                  )
                }
              />
            </label>
            <label class="insp-field">
              <span>Contrast</span>
              <input
                type="range"
                min="-100"
                max="100"
                value={Math.round(entity.contrast * 100)}
                onChange={(e) =>
                  patch({ contrast: entity.contrast }, { contrast: Number((e.target as HTMLInputElement).value) / 100 })
                }
              />
            </label>
            <small class="muted">
              {doc.assets[entity.assetHash]
                ? `${doc.assets[entity.assetHash]!.w}×${doc.assets[entity.assetHash]!.h} px · 1 px ≈ ${(
                    entity.wMm / doc.assets[entity.assetHash]!.w
                  ).toFixed(3)} mm${entity.homography ? " · perspective corrected" : ""}`
                : "asset missing"}
            </small>
            {!entity.locked && (
              <div class="insp-actions">
                <button onClick={() => tools.startPerspective(entity.id)}>Perspective</button>
                <button onClick={() => tools.startStraighten(entity.id)}>Straighten</button>
                <button onClick={() => tools.startCrop(entity.id)}>Crop</button>
                {entity.crop && <button onClick={() => tools.resetCrop(entity.id)}>Reset crop</button>}
                <button
                  onClick={() =>
                    patch(
                      {
                        opacity: entity.opacity,
                        grayscale: entity.grayscale,
                        brightness: entity.brightness,
                        contrast: entity.contrast
                      },
                      { opacity: 1, grayscale: false, brightness: 0, contrast: 0 }
                    )
                  }
                >
                  Reset
                </button>
              </div>
            )}
          </>
        )}
        <label class="insp-field">
          <span>Locked</span>
          <input type="checkbox" checked={entity.locked} onChange={() => tools.lockSelection(!entity.locked)} />
        </label>
        {"style" in entity && !entity.locked && (
          <StyleEditor style={(entity as Entity & { style: Style }).style} onChange={(s) => applyStyle([id], s)} />
        )}
        {entity.type === "group" && !entity.locked && <ColourBlock key={`colour-${id}`} api={api} ids={[id]} />}
        {!entity.locked && <AlignBlock api={api} tools={tools} />}
        {!entity.locked && <TransformBlock api={api} tools={tools} />}
      </div>
    );
  }

  // ---- multi-selection --------------------------------------------------------
  const first = doc.entities[sel[0]!];
  const styled = sel.filter((i) => doc.entities[i] && "style" in doc.entities[i]!);
  return (
    <div class="inspector">
      <div class="insp-title">{sel.length} selected</div>
      <label class="insp-field">
        <span>Layer</span>
        <select
          value={sel.every((i) => doc.entities[i]?.layerId === first?.layerId) ? first?.layerId : ""}
          onChange={(e) => tools.moveSelectionToLayer((e.target as HTMLSelectElement).value)}
        >
          <option value="" disabled>
            —
          </option>
          {doc.layers.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </label>
      {styled.length > 0 && first && "style" in first && (
        <StyleEditor style={(first as Entity & { style: Style }).style} onChange={(s) => applyStyle(styled, s)} />
      )}
      {sel.some((i) => doc.entities[i]?.type === "group") && <ColourBlock api={api} ids={sel} />}
      <AlignBlock api={api} tools={tools} />
      <TransformBlock api={api} tools={tools} />
      <div class="insp-actions">
        <button onClick={() => tools.groupSelection()} title="Ctrl+G">
          Group
        </button>
        <button onClick={() => tools.deleteSelection()} class="danger">
          Delete
        </button>
      </div>
    </div>
  );
}
